import {
  useMutation,
  useQuery,
  type UseMutationOptions,
} from "@tanstack/react-query";
import useJwt from "@/components/checkout/hooks/useJwt";
import {
  createCustomerAddress,
  deleteCustomerAddress,
  fetchCustomerAddresses,
  updateCustomerAddress,
  type CreateAddressBody,
  type CreateCustomerAddressResponse,
  type CustomerAddressResponse,
} from "@/networking/addresses.api";

export const customerAddressesQueryKey = (
  jwt: string | null,
  extra: readonly unknown[] = [],
) => ["customer-addresses", jwt, ...extra] as const;

type UseCustomerAddressesQueryArgs = {
  queryKey?: readonly unknown[];
  enabled?: boolean;
};

export function useCustomerAddressesQuery({
  queryKey,
  enabled = true,
}: UseCustomerAddressesQueryArgs = {}) {
  const { jwt } = useJwt();
  return useQuery({
    queryKey: queryKey ?? customerAddressesQueryKey(jwt),
    queryFn: () => fetchCustomerAddresses(jwt as string),
    enabled: enabled && !!jwt,
  });
}

export function useCreateAddressMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<
      CreateCustomerAddressResponse,
      Error,
      CreateAddressBody,
      TContext
    >,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<
    CreateCustomerAddressResponse,
    Error,
    CreateAddressBody,
    TContext
  >({
    mutationFn: (body) => createCustomerAddress(jwt as string, body),
    ...options,
  });
}

export type UpdateAddressVariables = {
  addressId: string;
  body: CreateAddressBody;
};

export function useUpdateAddressMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<
      CreateCustomerAddressResponse,
      Error,
      UpdateAddressVariables,
      TContext
    >,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<
    CreateCustomerAddressResponse,
    Error,
    UpdateAddressVariables,
    TContext
  >({
    mutationFn: ({ addressId, body }) =>
      updateCustomerAddress(addressId, jwt as string, body),
    ...options,
  });
}

export function useDeleteAddressMutation<TContext = unknown>(
  options?: Omit<
    UseMutationOptions<Record<string, never>, Error, string, TContext>,
    "mutationFn"
  >,
) {
  const { jwt } = useJwt();
  return useMutation<Record<string, never>, Error, string, TContext>({
    mutationFn: (addressId) => deleteCustomerAddress(addressId, jwt as string),
    ...options,
  });
}

export type { CustomerAddressResponse };
